import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Wallet, Send } from "lucide-react";
import { Th } from "./quotas/parts";
import { RevenuePanels } from "./quotas/RevenuePanels";
import { TierConfigForm } from "./quotas/TierConfigForm";
import { QuotaRow } from "./quotas/QuotaRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminQuotas() {
  const [year, setYear] = useState(null);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [revenue, setRevenue] = useState(null);
  const [tiers, setTiers] = useState({ aderente: 50, efetivo: 100, benemerito: 200, institucional: 500, honorario: 0 });
  const [savingTiers, setSavingTiers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sendingReminders, setSendingReminders] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/quotas`, { withCredentials: true });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
      setRevenue(r.data.revenue || null);
      setYear(r.data.year);
      setTiers(r.data.tiers || { aderente: 50, efetivo: 100, benemerito: 200, institucional: 500, honorario: 0 });
    } catch (e) {
      toast.error("Erro a carregar quotas.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveTiers = async (e) => {
    e.preventDefault();
    setSavingTiers(true);
    try {
      await axios.patch(`${API}/admin/quotas/config`, {
        aderente: Number(tiers.aderente) || 0,
        efetivo: Number(tiers.efetivo) || 0,
        benemerito: Number(tiers.benemerito) || 0,
        institucional: Number(tiers.institucional) || 0,
        honorario: Number(tiers.honorario) || 0,
      }, { withCredentials: true });
      toast.success("Valores atualizados.");
      load();
    } catch (e) {
      toast.error("Erro a guardar.");
    } finally { setSavingTiers(false); }
  };

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/quotas.csv`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `ciees-quotas-${year}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erro a descarregar CSV."); }
  };

  const sendReminders = async () => {
    if (!window.confirm(`Enviar email de lembrete a TODOS os sócios com quota ${year} por liquidar?`)) return;
    setSendingReminders(true);
    try {
      const r = await axios.post(`${API}/admin/quotas/send-reminders`, {}, { withCredentials: true });
      const skippedNote = r.data.skipped > 0
        ? ` · saltados: ${r.data.skipped} (lembrete enviado há menos de 7 dias ou modalidade mensal)` : "";
      toast.success(`Lembretes enviados: ${r.data.sent}${skippedNote}`, { duration: 8000 });
    } catch (e) {
      toast.error("Erro a enviar lembretes.");
    } finally { setSendingReminders(false); }
  };

  const markPaidTransfer = async (it) => {
    let month = null;
    if (it.modality === "monthly") {
      const m = window.prompt(
        `${it.name || it.email} está na modalidade MENSAL (meses pagos: ${(it.months_paid || []).join(", ") || "nenhum"}).\n\nIndique o Nº do MÊS recebido por transferência (1-12), ou deixe vazio para marcar a quota ANUAL completa:`,
        String((new Date()).getMonth() + 1),
      );
      if (m === null) return;
      if (m.trim()) {
        month = parseInt(m, 10);
        if (!(month >= 1 && month <= 12)) { toast.error("Mês inválido (1-12)."); return; }
      }
    }
    const ref = window.prompt(
      `Marcar ${month ? `a mensalidade de ${month}/${year}` : "a quota"} de ${it.name || it.email} como paga por transferência bancária?\n\nIndique uma referência interna (ex.: número da operação, data ou observação):`,
      new Date().toLocaleDateString("pt-PT"),
    );
    if (ref === null) return; // user cancelled
    try {
      await axios.post(
        `${API}/admin/quotas/mark-paid-transfer`,
        { member_id: it.member_id, reference: ref || "manual", ...(month ? { month } : {}) },
        { withCredentials: true },
      );
      toast.success(`${month ? "Mensalidade marcada" : "Quota marcada"} como paga · Declaração enviada por email.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a marcar como paga.");
    }
  };

  const filtered = filter === "all" ? items : items.filter((it) => it.state === filter);

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-14" data-testid="admin-quotas-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline">Cotas anuais</div>
          <h1 className="display-heading text-4xl sm:text-5xl mt-3">Quotas {year ?? ""}.</h1>
          <span className="accent-rule mt-6 block" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
            {loading ? "…" : `${counts.paid || 0} pagas · ${counts.pending || 0} pendentes · ${counts.exempt || 0} isentas · ${counts.no_tier || 0} sem nível atribuído`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={sendReminders}
            disabled={sendingReminders || !revenue || (revenue.pending_eur || 0) <= 0}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.18em] border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors disabled:opacity-50"
            data-testid="quotas-send-reminders"
            title="Envia email de lembrete a sócios com quota por liquidar"
          >
            <Send className="h-4 w-4" /> {sendingReminders ? "A enviar…" : "Enviar lembretes"}
          </button>
          <button onClick={downloadCsv} className="btn-outline" data-testid="quotas-download-csv">
            <Download className="h-4 w-4" /> Exportar CSV
          </button>
        </div>
      </div>

      {revenue && <RevenuePanels revenue={revenue} counts={counts} />}

      <TierConfigForm tiers={tiers} setTiers={setTiers} saving={savingTiers} onSubmit={saveTiers} />

      {/* Filter pills */}
      <div className="mt-10 flex flex-wrap gap-2">
        {[
          { id: "all", label: `Todas (${counts.total || 0})` },
          { id: "paid", label: `Pagas (${counts.paid || 0})` },
          { id: "pending", label: `Pendentes (${counts.pending || 0})` },
          { id: "exempt", label: `Isentas (${counts.exempt || 0})` },
          { id: "no_tier", label: `Sem nível (${counts.no_tier || 0})` },
        ].map((p) => (
          <button key={p.id} onClick={() => setFilter(p.id)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.16em] border transition-colors ${
              filter === p.id
                ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                : "bg-white text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"
            }`}
            data-testid={`quotas-filter-${p.id}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="mt-6 bg-white border border-[hsl(var(--border))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
              <Th>Sócio</Th>
              <Th>Email</Th>
              <Th>Nível</Th>
              <Th>Valor</Th>
              <Th>Estado</Th>
              <Th>Declaração</Th>
              <th className="text-right text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] font-medium px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">A carregar…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-[hsl(var(--muted-foreground))]">Sem registos.</td></tr>
            ) : (
              filtered.map((it) => <QuotaRow key={it.member_id} it={it} onMarkPaid={markPaidTransfer} />)
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-6 text-xs text-[hsl(var(--muted-foreground))]">
        <Wallet className="inline h-3 w-3 mr-1" />
        Para atribuir/alterar o nível de um sócio, vá a <strong>Sócios</strong>.
      </p>
    </div>
  );
}
